'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowUpRight, Check, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import type { Filter, Vendor, VendorDataset } from '@/lib/data/vendor-datasets/types';

interface VendorPickerProps {
  dataset: VendorDataset;
}

type FilterValue = boolean | number | string | null;
type FilterState = Record<string, FilterValue>;

const TEAM_SIZE_OPTIONS = [
  { label: 'Startup', value: 'startup' },
  { label: '5-20', value: '5-20' },
  { label: '20-100', value: '20-100' },
  { label: 'Enterprise', value: 'enterprise' },
];

function RankBadge({ rank }: { rank: number }) {
  const labels = ['1st', '2nd', '3rd'];
  const colors = [
    'bg-green-100 text-green-800 border-green-200',
    'bg-gray-100 text-gray-700 border-gray-200',
    'bg-gray-100 text-gray-700 border-gray-200',
  ];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${colors[rank - 1] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {labels[rank - 1] || `${rank}th`}
    </span>
  );
}

function isActiveValue(value: FilterValue): boolean {
  return value !== undefined && value !== null && value !== false && value !== '';
}

function getFeatureValue(vendor: Vendor, key: string): boolean | string | number | undefined {
  if (key === 'freeTier') return vendor.pricing.freeTier;
  return vendor.features[key];
}

function checkFilter(vendor: Vendor, filter: Filter, value: FilterValue): boolean {
  if (!isActiveValue(value)) return true;

  if (filter.type === 'boolean') {
    return getFeatureValue(vendor, filter.key) === true;
  }

  if (filter.type === 'budget') {
    const startsAt = vendor.pricing.startsAt ?? 999999;
    return typeof value === 'number' && startsAt <= value;
  }

  if (filter.type === 'select') {
    return getFeatureValue(vendor, filter.key) === value;
  }

  if (filter.type === 'team-size') {
    return typeof value === 'string' && (vendor.fitsTeamSizes ?? []).includes(value);
  }

  return true;
}

function formatPricing(vendor: Vendor): string {
  if (vendor.pricing.freeTier) return 'Free';
  if (typeof vendor.pricing.startsAt !== 'number') return 'Contact sales';
  return `$${vendor.pricing.startsAt}/mo${vendor.pricing.perUser ? ' per user' : ''}`;
}

function encodeState(state: FilterState): string {
  return window.btoa(JSON.stringify(state));
}

function decodeState(value: string): FilterState | null {
  try {
    const parsed: unknown = JSON.parse(window.atob(value));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as FilterState;
  } catch {
    return null;
  }
}

function toFilterState(filters: Record<string, unknown>): FilterState {
  const next: FilterState = {};
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string' || value === null) {
      next[key] = value;
    }
  }
  return next;
}

export default function VendorPicker({ dataset }: VendorPickerProps) {
  const [filterState, setFilterState] = useState<FilterState>({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get('picker');
    if (!encoded) return;

    const decoded = decodeState(encoded);
    if (decoded) setFilterState(decoded);
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const url = new URL(window.location.href);
      const activeState = Object.fromEntries(Object.entries(filterState).filter(([, value]) => isActiveValue(value)));
      if (Object.keys(activeState).length > 0) {
        url.searchParams.set('picker', encodeState(activeState));
      } else {
        url.searchParams.delete('picker');
      }
      window.history.replaceState(null, '', url.toString());
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [filterState]);

  const activeFilters = useMemo(
    () => dataset.filters.filter(filter => isActiveValue(filterState[filter.key])),
    [dataset.filters, filterState]
  );

  const results = useMemo(() => {
    return dataset.vendors
      .map((vendor, index) => {
        let score = 0;
        let passes = 0;
        const failedHardFilters: Filter[] = [];

        for (const filter of dataset.filters) {
          const pass = checkFilter(vendor, filter, filterState[filter.key]);
          if (filter.hard && pass) passes += 1;
          if (pass) score += filter.weight ?? 1;
          if (!pass && filter.hard) {
            score -= 100;
            failedHardFilters.push(filter);
          }
        }

        return {
          vendor,
          originalIndex: index,
          passes,
          passesAll: failedHardFilters.length === 0,
          failedHardFilters,
          score,
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const aPrice = a.vendor.pricing.startsAt ?? 999999;
        const bPrice = b.vendor.pricing.startsAt ?? 999999;
        if (aPrice !== bPrice) return aPrice - bPrice;
        return a.originalIndex - b.originalIndex;
      });
  }, [dataset.filters, dataset.vendors, filterState]);

  const matchingCount = results.filter(result => result.passesAll).length;

  const setFilter = useCallback((key: string, value: FilterValue) => {
    setFilterState(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilterState({});
  }, []);

  const shareLink = useCallback(async () => {
    const url = new URL(window.location.href);
    const activeState = Object.fromEntries(Object.entries(filterState).filter(([, value]) => isActiveValue(value)));
    if (Object.keys(activeState).length > 0) {
      url.searchParams.set('picker', encodeState(activeState));
    } else {
      url.searchParams.delete('picker');
    }
    await navigator.clipboard.writeText(url.toString());
  }, [filterState]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 my-8 not-prose">
      <h3 className="text-lg font-bold text-gray-900 mb-1">{dataset.topic}</h3>
      <p className="text-gray-600 mb-6">{dataset.intro}</p>

      <button
        type="button"
        onClick={() => setFiltersOpen(open => !open)}
        className="md:hidden w-full mb-4 inline-flex items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          Filters ({activeFilters.length} active)
        </span>
        <span className="text-gray-500">{filtersOpen ? 'Hide' : 'Show'}</span>
      </button>

      <div className="md:grid md:grid-cols-[minmax(240px,320px)_1fr] md:gap-6">
        <aside className={`${filtersOpen ? 'block' : 'hidden'} md:block mb-6 md:mb-0`}>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-5">
            {dataset.presets && dataset.presets.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Presets</p>
                <div className="flex flex-wrap gap-2">
                  {dataset.presets.map(preset => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setFilterState(prev => ({ ...prev, ...toFilterState(preset.filters) }))}
                      className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-blue-300 hover:text-blue-700"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {dataset.filters.map(filter => {
              const value = filterState[filter.key];
              const id = `${dataset.key}-${filter.key}`;

              if (filter.type === 'boolean') {
                return (
                  <div key={filter.key} className="flex items-start gap-2">
                    <input
                      id={id}
                      type="checkbox"
                      checked={value === true}
                      onChange={event => setFilter(filter.key, event.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor={id} className="text-sm font-medium text-gray-700" title={filter.description}>
                      {filter.label}
                    </label>
                  </div>
                );
              }

              if (filter.type === 'budget') {
                const budget = typeof value === 'number' ? value : 500;
                return (
                  <div key={filter.key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor={id} className="text-sm font-medium text-gray-700" title={filter.description}>
                        {filter.label}
                      </label>
                      <span className="text-xs text-gray-600">{typeof value === 'number' ? `Up to $${value}/mo` : 'Any budget'}</span>
                    </div>
                    <input
                      id={id}
                      type="range"
                      min={0}
                      max={500}
                      step={5}
                      value={budget}
                      onChange={event => setFilter(filter.key, Number(event.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                  </div>
                );
              }

              if (filter.type === 'select') {
                return (
                  <div key={filter.key} className="space-y-2">
                    <label htmlFor={id} className="text-sm font-medium text-gray-700" title={filter.description}>
                      {filter.label}
                    </label>
                    <select
                      id={id}
                      value={typeof value === 'string' ? value : ''}
                      onChange={event => setFilter(filter.key, event.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Any</option>
                      {(filter.options ?? []).map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              return (
                <div key={filter.key} className="space-y-2">
                  <p className="text-sm font-medium text-gray-700" id={`${id}-label`} title={filter.description}>
                    {filter.label}
                  </p>
                  <div className="grid grid-cols-2 gap-2" role="group" aria-labelledby={`${id}-label`}>
                    {TEAM_SIZE_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFilter(filter.key, value === option.value ? '' : option.value)}
                        className={`rounded-md border px-2 py-1.5 text-xs font-medium ${
                          value === option.value
                            ? 'border-blue-300 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="flex gap-2 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={shareLink}
                className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Share link
              </button>
            </div>
          </div>
        </aside>

        <section>
          <p className="text-sm text-gray-600 mb-3">
            Showing {matchingCount} of {dataset.vendors.length} vendors that match
          </p>

          <div className="space-y-3">
            {results.map((result, index) => (
              <article
                key={result.vendor.slug}
                className={`rounded-lg border p-4 ${
                  result.passesAll ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                {!result.passesAll && (
                  <p className="mb-2 text-xs font-medium text-gray-600">
                    Doesn't meet: {result.failedHardFilters.map(filter => filter.label).join(', ')}
                  </p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <RankBadge rank={index + 1} />
                      <h4 className="text-base font-bold text-gray-900">{result.vendor.name}</h4>
                    </div>
                    <p className="text-sm text-gray-600">{result.vendor.blurb}</p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-sm font-bold text-gray-900">{formatPricing(result.vendor)}</p>
                    {result.vendor.pricing.pricingNote && (
                      <p className="text-xs text-gray-500 sm:max-w-48">{result.vendor.pricing.pricingNote}</p>
                    )}
                  </div>
                </div>

                {activeFilters.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {activeFilters.map(filter => {
                      const matches = checkFilter(result.vendor, filter, filterState[filter.key]);
                      return (
                        <span
                          key={filter.key}
                          aria-label={`${filter.label}: ${matches ? 'matches' : 'does not match'}`}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
                            matches
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-gray-200 bg-gray-100 text-gray-600'
                          }`}
                        >
                          {matches ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          {filter.label}
                        </span>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {result.vendor.highlights && result.vendor.highlights.length > 0 && (
                    <ul className="space-y-2">
                      {result.vendor.highlights.slice(0, 3).map(highlight => (
                        <li key={highlight} className="flex gap-2 text-sm text-gray-700">
                          <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {result.vendor.tradeoffs && result.vendor.tradeoffs.length > 0 && (
                    <ul className="space-y-2">
                      {result.vendor.tradeoffs.slice(0, 3).map(tradeoff => (
                        <li key={tradeoff} className="flex gap-2 text-sm text-gray-600">
                          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
                          <span>{tradeoff}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-4">
                  <a
                    href={result.vendor.affiliateLink || result.vendor.link}
                    target="_blank"
                    rel="noopener sponsored"
                    className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                  >
                    Visit
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
